import { LitElement, html, css } from 'lit';

export class UICascader extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: String, reflect: true }, // Format: value1,value2,value3
    placeholder: { type: String },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    options: { type: Array }, // Hierarchical tree of { value, label, children }
    separator: { type: String }, // e.g. " / "
    label: { type: String, reflect: true },
    
    // Internal States
    isOpen: { type: Boolean, state: true },
    activePath: { type: Array, state: true }, // Array of selected values: [v1, v2, ...]
    inputValue: { type: String, state: true },
    focusedColumnIndex: { type: Number, state: true },
    focusedRowIndex: { type: Number, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      position: relative;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .cascader-container {
      position: relative;
      width: 100%;
    }

    .cascader-header {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }

    .cascader-input {
      width: 100%;
      height: var(--ui-input-height);
      padding: 0 var(--ui-padding-x);
      padding-right: 40px;
      background-color: var(--ui-surface);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      font-family: inherit;
      font-size: inherit;
      color: inherit;
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      outline: none;
      box-sizing: border-box;
      cursor: pointer;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }

    .cascader-input:hover:not(:disabled) {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .cascader-input:focus:not(:disabled) {
      border-color: var(--ui-border-focus);
      background-color: var(--ui-surface);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .cascader-input:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    .cascader-arrow {
      position: absolute;
      right: var(--ui-padding-x);
      top: 50%;
      transform: translateY(-50%);
      color: var(--ui-text-muted);
      pointer-events: none;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: var(--ui-transition-fast);
    }

    .cascader-container.open .cascader-arrow {
      transform: translateY(-50%) rotate(180deg);
      color: var(--ui-primary);
    }

    /* Popover panel */
    .cascader-panel {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      background-color: color-mix(in srgb, var(--ui-surface) 90%, transparent);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-lg);
      box-shadow: var(--ui-shadow-popover);
      z-index: var(--ui-z-popover);
      transform-origin: top;
      opacity: 0;
      transform: scale(0.95);
      pointer-events: none;
      transition: opacity var(--ui-transition-fast), transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
      box-sizing: border-box;
      display: flex;
      overflow: hidden;
    }

    .cascader-panel.open {
      opacity: 1;
      transform: scale(1);
      pointer-events: auto;
    }

    /* Column views on Desktop */
    .cascader-columns-wrap {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      max-height: 220px;
    }

    .cascader-column {
      min-width: 140px;
      max-width: 180px;
      overflow-y: auto;
      border-right: 1px solid var(--ui-border);
      padding: 6px;
      box-sizing: border-box;
      scrollbar-width: thin;
    }

    .cascader-column:last-child {
      border-right: none;
    }

    /* Menu item option */
    .cascader-option {
      padding: 8px 12px;
      border-radius: var(--ui-radius-sm);
      cursor: pointer;
      font-size: var(--ui-font-size-base);
      transition: var(--ui-transition-fast);
      display: flex;
      align-items: center;
      justify-content: space-between;
      color: var(--ui-text);
      gap: 12px;
    }

    .cascader-option:hover {
      background-color: var(--ui-surface-hover);
    }

    .cascader-option.focused {
      background-color: var(--ui-surface-active);
      color: var(--ui-primary);
    }

    .cascader-option.selected {
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
      font-weight: 600;
    }

    .cascader-option.active-parent {
      background-color: color-mix(in srgb, var(--ui-primary) 10%, var(--ui-surface));
      color: var(--ui-primary);
      font-weight: 500;
    }

    .cascader-option-label {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .option-arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.7;
    }

    /* Mobile view header / overrides */
    .mobile-nav-header {
      display: none;
      align-items: center;
      padding: 12px var(--ui-padding-x);
      border-bottom: 1px solid var(--ui-border);
      gap: 12px;
    }

    .btn-back {
      border: none;
      background: transparent;
      padding: 4px 8px;
      color: var(--ui-primary);
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      border-radius: var(--ui-radius-sm);
      font-size: var(--ui-font-size-sm);
      transition: var(--ui-transition-fast);
    }

    .btn-back:hover {
      background-color: var(--ui-surface-hover);
    }

    .mobile-title {
      font-weight: 600;
      font-size: var(--ui-font-size-base);
    }

    /* Responsive Mobile Sheet */
    .backdrop {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      z-index: calc(var(--ui-z-popover) - 1);
    }

    @media (max-width: 576px) {
      .cascader-container.open .backdrop {
        display: block;
      }

      .cascader-panel {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        top: auto;
        width: 100%;
        max-width: 100%;
        border-radius: var(--ui-radius-lg) var(--ui-radius-lg) 0 0;
        box-shadow: 0 -10px 25px rgba(0,0,0,0.15);
        transform: translateY(100%);
        transform-origin: bottom;
        transition: transform var(--ui-transition-normal) cubic-bezier(0.25, 0.8, 0.25, 1), opacity var(--ui-transition-normal);
        z-index: var(--ui-z-popover);
        flex-direction: column;
      }

      .cascader-container.open .cascader-panel {
        transform: translateY(0);
        opacity: 1;
      }

      .cascader-panel::before {
        content: '';
        display: block;
        width: 40px;
        height: 4px;
        background-color: var(--ui-border);
        border-radius: var(--ui-radius-full);
        margin: -4px auto 12px auto;
      }

      .mobile-nav-header {
        display: flex;
      }

      .cascader-columns-wrap {
        width: 100%;
        display: block; /* Stack or only render active */
        max-height: 280px;
      }

      /* On mobile, only display the active column list level to fit the screen */
      .cascader-column {
        width: 100%;
        max-width: 100%;
        border-right: none;
        display: none;
      }

      .cascader-column.mobile-active {
        display: block;
      }
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = '';
    this.placeholder = 'Sélectionner...';
    this.disabled = false;
    this.required = false;
    this.options = [];
    this.separator = ' / ';
    this.label = '';

    this.isOpen = false;
    this.activePath = [];
    this.inputValue = '';
    this.focusedColumnIndex = 0;
    this.focusedRowIndex = -1;

    this._handleOutsideClick = this._handleOutsideClick.bind(this);
    this._handleResize = this._handleResize.bind(this);
  }

  willUpdate(changedProperties) {
    if (typeof this.options === 'string') {
      try {
        this.options = JSON.parse(this.options);
      } catch (e) {
        this.options = [];
      }
    }

    if (changedProperties.has('value') || changedProperties.has('options')) {
      this._syncActivePathFromValue();
    }
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value);
      this._updateValidity();
    }
    if (this.isOpen && (changedProperties.has('isOpen') || changedProperties.has('activePath'))) {
      this._adjustPosition();
    }
  }

  _updateValidity() {
    const hasValue = this.value && this.value.trim() !== '';
    if (this.required && !hasValue) {
      this.internals.setValidity(
        { valueMissing: true }, 
        'Veuillez faire une sélection.',
        this.shadowRoot.querySelector('.cascader-input')
      );
    } else {
      this.internals.setValidity({});
    }
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this._handleOutsideClick);
    window.addEventListener('resize', this._handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this._handleOutsideClick);
    window.removeEventListener('resize', this._handleResize);
  }

  _handleResize() {
    if (this.isOpen) {
      this._adjustPosition();
    }
  }

  _adjustPosition() {
    if (window.innerWidth <= 576) {
      // Mobile bottom sheet mode: clear custom styles so CSS overrides take control
      const panel = this.shadowRoot.querySelector('.cascader-panel');
      if (panel) {
        panel.style.left = '';
        panel.style.right = '';
      }
      return;
    }

    requestAnimationFrame(() => {
      const panel = this.shadowRoot.querySelector('.cascader-panel');
      if (!panel) return;

      // Reset to default left alignment to measure natural width
      panel.style.left = '0';
      panel.style.right = 'auto';

      const rect = panel.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // If it overflows the viewport boundary on the right, align it to the right of the trigger
      if (rect.right > viewportWidth) {
        panel.style.left = 'auto';
        panel.style.right = '0';
      }
    });
  }

  _syncActivePathFromValue() {
    if (this.value) {
      this.activePath = this.value.split(',');
      this.inputValue = this._getPathLabel(this.activePath);
    } else {
      this.activePath = [];
      this.inputValue = '';
    }
  }

  _getPathLabel(pathArr) {
    const labels = [];
    let currentOpts = this.options;
    
    for (const val of pathArr) {
      if (!currentOpts || !Array.isArray(currentOpts)) {
        labels.push(val);
        currentOpts = [];
        continue;
      }
      const match = currentOpts.find(o => String(o.value) === String(val));
      if (match) {
        labels.push(match.label);
        currentOpts = match.children || [];
      } else {
        labels.push(val);
        currentOpts = [];
      }
    }
    
    return labels.join(this.separator);
  }

  _handleOutsideClick(e) {
    if (this.isOpen && !this.contains(e.target) && !e.composedPath().includes(this)) {
      this._closePanel();
    }
  }

  _togglePanel() {
    if (this.disabled) return;
    if (this.isOpen) {
      this._closePanel();
    } else {
      this._openPanel();
    }
  }

  _openPanel() {
    this.isOpen = true;
    this.focusedColumnIndex = 0;
    this.focusedRowIndex = -1;
    if (this.value) {
      this.activePath = this.value.split(',');
    } else {
      this.activePath = [];
    }
  }

  _closePanel() {
    this.isOpen = false;
  }

  _getColumns() {
    if (!this.options || !Array.isArray(this.options)) return [];
    const list = [this.options];
    let currentOpts = this.options;

    for (const val of this.activePath) {
      if (!currentOpts || !Array.isArray(currentOpts)) break;
      const node = currentOpts.find(o => String(o.value) === String(val));
      if (node && node.children && node.children.length > 0) {
        list.push(node.children);
        currentOpts = node.children;
      } else {
        break;
      }
    }
    return list;
  }

  _selectItem(colIndex, item) {
    // Slice activePath up to the parent level
    const newPath = this.activePath.slice(0, colIndex);
    newPath.push(item.value);
    
    this.activePath = newPath;

    // Reset keyboard focus coordinates
    this.focusedColumnIndex = colIndex;
    this.focusedRowIndex = -1;

    if (!item.children || item.children.length === 0) {
      // Leaf node, selection complete!
      const val = this.activePath.join(',');
      this.value = val;
      this.inputValue = this._getPathLabel(this.activePath);
      this._closePanel();
      this._dispatchChangeEvent(val);
    } else {
      // Internal parent node, update layout
      this.requestUpdate();
    }
  }

  _goBackMobile() {
    if (this.activePath.length > 0) {
      this.activePath = this.activePath.slice(0, -1);
      this.requestUpdate();
    }
  }

  _dispatchChangeEvent(val) {
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: val },
      bubbles: true,
      composed: true
    }));
  }

  // Keyboard navigation
  _handleKeyDown(e) {
    if (this.disabled) return;

    const columns = this._getColumns();
    const activeColumn = columns[this.focusedColumnIndex] || [];

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!this.isOpen) {
        this._openPanel();
      } else {
        this.focusedRowIndex = (this.focusedRowIndex + 1) % activeColumn.length;
        this._scrollToItem(this.focusedColumnIndex, this.focusedRowIndex);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!this.isOpen) {
        this._openPanel();
      } else {
        this.focusedRowIndex = (this.focusedRowIndex - 1 + activeColumn.length) % activeColumn.length;
        this._scrollToItem(this.focusedColumnIndex, this.focusedRowIndex);
      }
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (this.isOpen && this.focusedRowIndex >= 0) {
        const item = activeColumn[this.focusedRowIndex];
        if (item && item.children && item.children.length > 0) {
          // Select this item to open its children column
          this._selectItem(this.focusedColumnIndex, item);
          // Focus the next column
          this.focusedColumnIndex += 1;
          this.focusedRowIndex = 0;
          this._scrollToItem(this.focusedColumnIndex, this.focusedRowIndex);
        }
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (this.isOpen && this.focusedColumnIndex > 0) {
        // Go back one column
        this.focusedColumnIndex -= 1;
        // Find row index of previous parent
        const parentVal = this.activePath[this.focusedColumnIndex];
        const prevColumn = columns[this.focusedColumnIndex];
        this.focusedRowIndex = prevColumn.findIndex(o => String(o.value) === String(parentVal));
        this.activePath = this.activePath.slice(0, this.focusedColumnIndex);
        this._scrollToItem(this.focusedColumnIndex, this.focusedRowIndex);
      }
    } else if (e.key === 'Enter') {
      if (this.isOpen) {
        if (this.focusedRowIndex >= 0 && this.focusedRowIndex < activeColumn.length) {
          this._selectItem(this.focusedColumnIndex, activeColumn[this.focusedRowIndex]);
          e.preventDefault();
        }
      } else {
        this._openPanel();
        e.preventDefault();
      }
    } else if (e.key === 'Escape') {
      if (this.isOpen) {
        this._closePanel();
        e.preventDefault();
      }
    }
  }

  _scrollToItem(colIndex, rowIndex) {
    setTimeout(() => {
      const colEl = this.shadowRoot.querySelectorAll('.cascader-column')[colIndex];
      const itemEl = colEl?.querySelectorAll('.cascader-option')[rowIndex];
      if (colEl && itemEl) {
        const colTop = colEl.scrollTop;
        const colBottom = colTop + colEl.clientHeight;
        const elTop = itemEl.offsetTop;
        const elBottom = elTop + itemEl.clientHeight;
        
        if (elTop < colTop) {
          colEl.scrollTop = elTop;
        } else if (elBottom > colBottom) {
          colEl.scrollTop = elBottom - colEl.clientHeight;
        }
      }
    }, 0);
  }

  render() {
    const columns = this._getColumns();
    const mobileActiveIndex = columns.length - 1;
    
    // Breadcrumbs for mobile view header
    const currentBreadcrumb = this.activePath.length > 0 ? this._getPathLabel(this.activePath) : 'Catégorie';

    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="cascader-container ${this.isOpen ? 'open' : ''}">
        <div class="cascader-header" @click="${this._togglePanel}">
          <input
            type="text"
            class="cascader-input" part="input"
            .value="${this.inputValue}"
            placeholder="${this.placeholder}"
            ?disabled="${this.disabled}"
            readonly
            @keydown="${this._handleKeyDown}"
          />
          <div class="cascader-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>

        <div class="backdrop" @click="${this._closePanel}"></div>

        <div class="cascader-panel ${this.isOpen ? 'open' : ''}">
          <!-- Mobile view only navigation header -->
          <div class="mobile-nav-header">
            ${this.activePath.length > 0 ? html`
              <button type="button" class="btn-back" @click="${this._goBackMobile}">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                Retour
              </button>
            ` : ''}
            <span class="mobile-title">${currentBreadcrumb}</span>
          </div>

          <div class="cascader-columns-wrap">
            ${columns.map((colOpts, colIdx) => {
              const isColFocused = colIdx === this.focusedColumnIndex;
              const isMobileActive = colIdx === mobileActiveIndex;
              
              return html`
                <div 
                  class="cascader-column ${isMobileActive ? 'mobile-active' : ''}"
                >
                  ${colOpts.map((item, rowIdx) => {
                    const isSelected = this.activePath[colIdx] === item.value;
                    const hasChildren = item.children && item.children.length > 0;
                    
                    // Highlight if it's the current active branch path
                    const isActiveParent = isSelected && hasChildren;
                    // Fully selected (e.g. leaf)
                    const isFullLeafSelected = isSelected && !hasChildren && (colIdx === this.activePath.length - 1);
                    
                    const isFocused = isColFocused && rowIdx === this.focusedRowIndex;

                    return html`
                      <div 
                        class="cascader-option 
                          ${isFullLeafSelected ? 'selected' : ''} 
                          ${isActiveParent ? 'active-parent' : ''}
                          ${isFocused ? 'focused' : ''}
                        "
                        @click="${() => this._selectItem(colIdx, item)}"
                      >
                        <span class="cascader-option-label" title="${item.label}">${item.label}</span>
                        ${hasChildren ? html`
                          <span class="option-arrow">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                          </span>
                        ` : isFullLeafSelected ? html`
                          <span class="option-arrow">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          </span>
                        ` : ''}
                      </div>
                    `;
                  })}
                </div>
              `;
            })}
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('ui-cascader')) {
  customElements.define('ui-cascader', UICascader);
}
